import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { PriestReflection } from '../types';
import { apiService } from '../services/api';

const PriestMessage: React.FC = () => {
  const [currentReflection, setCurrentReflection] = useState<PriestReflection | null>(null);

  useEffect(() => {
    const fetchLatestReflection = async () => {
      try {
        const reflections = await apiService.getReflections();
        if (reflections.length > 0) {
          setCurrentReflection(reflections[0]);
        }
      } catch (error) {
        console.error('Error fetching reflections:', error);
        // Fallback to production reflection data
        const productionReflection: PriestReflection = {
      id: '1',
      title: 'Finding Light in the Darkness',
      content: `Dear beloved parishioners,

As we journey through this Advent season, I am reminded of the profound truth that even in our darkest moments, Christ's light shines brightest. The waiting and anticipation of Advent teaches us patience, hope, and trust in God's timing.

In our modern world, filled with uncertainties and challenges, we are called to be beacons of that same light for others. Each act of kindness, each moment of compassion, each prayer offered in love becomes a reflection of Christ's presence among us.

I encourage you all to take time this week for quiet reflection. In the stillness, we often find the answers we seek and the peace our hearts desire. Remember that our church family is here to support one another, especially during difficult times.

May the light of Christ fill your hearts and homes this season.

In faith and service,
Father Rejimon Devasia`,
      date: '2024-11-24',
      excerpt: 'As we journey through this Advent season, I am reminded of the profound truth that even in our darkest moments, Christ\'s light shines brightest...'
        };

        setCurrentReflection(productionReflection);
      }
    };

    fetchLatestReflection();
  }, []);

  if (!currentReflection) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <section className="py-16 bg-church-light" aria-labelledby="priest-message-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 id="priest-message-heading" className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
              Message from Our Priest
            </h2>
            <p className="text-lg text-gray-600">
              Spiritual guidance and reflections 
            </p>
          </div>

          <article className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-8">
              <header className="mb-6">
                <h3 className="text-2xl font-serif font-semibold text-church-dark mb-2">
                  {currentReflection.title}
                </h3>
                <time 
                  dateTime={currentReflection.date}
                  className="text-sm text-gray-500"
                >
                  {formatDate(currentReflection.date)}
                </time>
              </header>

              <div className="prose prose-lg max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {currentReflection.content.length > 500 
                    ? `${currentReflection.content.substring(0, 500)}...`
                    : currentReflection.content
                  }
                </div>
              </div>

              <footer className="mt-8 pt-6 border-t border-gray-200">
                <Link
                  to="/reflections"
                  className="inline-flex items-center bg-church-blue hover:bg-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
                >
                  <BookOpenIcon className="h-5 w-5 mr-2" />
                  Read All Reflections
                </Link>
              </footer>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default PriestMessage;